import { Button, Title1, BodyR } from '@pensionbee/honeypot-ui';
import Head from 'next/head';
import { ThemedImage } from '@/domains/theme/ThemedImage';
import { Dialog } from '@capacitor/dialog';
import { Browser } from '@capacitor/browser';
import { App } from '@capacitor/app';
import { compareVersions } from 'compare-versions';
import { type ReactNode, useEffect, useState } from 'react';
import LoadingScreen from '@/domains/shared/components/LoadingScreen';
import { useMinimumAppVersionQuery } from '@/domains/shared/api/queries/useMinimumAppVersionQuery';
import { isIOS, isNative } from '@/domains/shared/utils/native';

const shouldSkipForceUpdateCheck = () =>
  !isNative() ||
  process.env.NEXT_PUBLIC_PLATFORM_APP_ENV === 'dev' ||
  process.env.NEXT_PUBLIC_PLATFORM_APP_ENV === 'staging';

type ForceUpdateScreenProps = {
  children: ReactNode;
};

/** Must sit under QueryClientProvider + AuthProvider (version query uses both). */
const ForceUpdateScreen = ({ children }: ForceUpdateScreenProps) => {
  const skipCheck = shouldSkipForceUpdateCheck();
  const [currentAppVersion, setCurrentAppVersion] = useState<string>();
  const { data, isPending, isError } = useMinimumAppVersionQuery();
  const isIosPlatform = isIOS();

  useEffect(() => {
    if (skipCheck) return;

    void App.getInfo().then((appInfo) => {
      setCurrentAppVersion(appInfo.version);
    });
  }, [skipCheck]);

  const isLoading =
    !skipCheck && (currentAppVersion === undefined || (isPending && !isError));

  const isUpdateRequired =
    !skipCheck &&
    !isLoading &&
    !isError &&
    !!currentAppVersion &&
    !!data?.minimumAppVersion &&
    compareVersions(currentAppVersion, data.minimumAppVersion) === -1;

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isUpdateRequired) {
    return children;
  }

  const showAlert = async () => {
    await Dialog.alert({
      title: 'Oops!',
      message:
        "We weren't able to open the store automatically for you. Please go to the store and download the updated version!",
    });
  };

  const goToStore = async (url: string) => {
    try {
      await Browser.open({ url: url });
    } catch (error) {
      console.warn(error);
      showAlert();
    }
  };

  return (
    <>
      <Head>
        <title>PensionBee - Force Update</title>
      </Head>
      <section className="flex bg-background h-screen flex-col items-center justify-between px-20">
        <div className="items-center flex-1 flex justify-center flex-col">
          <ThemedImage
            name="mobileChecklistIcon"
            style={{ height: 200 }}
            alt={'Mobile Checklist icon'}
            className="w-5/6"
          />
          <Title1 className="text-center mb-10">Please update your</Title1>
          <Title1 className="text-center mb-20">PensionBee app</Title1>
          <BodyR className="text-center mb-20">
            You’re running an old version of the app, which misses out on new
            features and important security updates. Please update to the latest
            version.
          </BodyR>
        </div>
        <div className="items-center flex justify-center w-full">
          {isIosPlatform ? (
            <Button
              className="w-full bg-black text-white mb-20"
              onClick={() =>
                goToStore(
                  'https://apps.apple.com/gb/app/pensionbee-combine-pensions/id1345490587',
                )
              }
            >
              Go to the App Store
            </Button>
          ) : (
            <Button
              className="w-full bg-black text-white mb-20"
              onClick={() =>
                goToStore(
                  'https://play.google.com/store/apps/details?id=com.pensionbee.mobile.production',
                )
              }
            >
              Go to the Play Store
            </Button>
          )}
        </div>
      </section>
    </>
  );
};

export default ForceUpdateScreen;
